import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, Mail, Phone, RefreshCw, ShieldCheck } from 'lucide-react';
import type { TurnstileHandle } from '../components/ui/Turnstile';
import { useAppContext } from './AppContext';
import { VerificationContext, VerificationIntent, VerificationOptions } from './VerificationContextValue';
import { CONTACT_INFO } from '../constants';
import { supabase } from '../lib/supabase';
import { getOrCreateVisitorId } from '../lib/visitorId';
import { prefetchTurnstileScript } from '../components/ui/turnstileLoader';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const MAX_FAIL_ATTEMPTS = 3;
const QUOTE_VERIFICATION_SESSION_TTL_MS = 30 * 60 * 1000;

export const VerificationProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useAppContext();
    const verificationText = t?.verification;
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [quoteVerificationSession, setQuoteVerificationSession] = useState<string | null>(null);
    const [quoteVerificationExpiresAt, setQuoteVerificationExpiresAt] = useState<number | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [verificationFailed, setVerificationFailed] = useState(false);
    const [verificationSuccess, setVerificationSuccess] = useState(false);
    const [failCount, setFailCount] = useState(0);
    const [intent, setIntent] = useState<VerificationIntent>('quote');
    const [resetKey, setResetKey] = useState(0);
    const [backgroundResetKey, setBackgroundResetKey] = useState(0);
    const [shouldWarmQuoteVerification, setShouldWarmQuoteVerification] = useState(false);
    const [TurnstileComponent, setTurnstileComponent] = useState<ComponentType<any> | null>(null);
    const resolveRef = useRef<((value: string | null) => void) | null>(null);
    const turnstileRef = useRef<TurnstileHandle | null>(null);
    const backgroundTurnstileRef = useRef<TurnstileHandle | null>(null);
    const lastTurnstileErrorEventRef = useRef<{ intent: VerificationIntent; at: number } | null>(null);
    const backgroundWarmupBlockedUntilRef = useRef(0);
    const backgroundExecuteInFlightRef = useRef(false);
    const quoteVerificationSessionPromiseRef = useRef<Promise<string | null> | null>(null);
    const quoteVerificationSessionValueRef = useRef<string | null>(null);
    const quoteVerificationExpiresAtRef = useRef<number | null>(null);
    const shouldRenderTurnstile = isVisible || verificationSuccess || verificationFailed;
    const loadAnalytics = useCallback(() => import('../lib/analytics'), []);

    useEffect(() => {
        const pathname = window.location.pathname.toLowerCase();
        if (pathname.startsWith('/admin') || pathname.startsWith('/translator')) {
            return;
        }

        let cancelled = false;
        const timeoutId = window.setTimeout(() => {
            void import('../components/ui/Turnstile').then((module) => {
                if (cancelled) return;
                setTurnstileComponent((current: ComponentType<any> | null) => current ?? module.Turnstile);
                void prefetchTurnstileScript().catch((error) => {
                    console.warn('Failed to prefetch Turnstile script', error);
                });
            });
        }, 1200);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, []);

    useEffect(() => {
        if (!shouldRenderTurnstile || TurnstileComponent) return;

        let cancelled = false;
        void import('../components/ui/Turnstile').then((module) => {
            if (!cancelled) {
                setTurnstileComponent(() => module.Turnstile);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [TurnstileComponent, shouldRenderTurnstile]);

    const resetToken = useCallback(() => {
        setTurnstileToken(null);
        setResetKey((prev) => prev + 1);
        turnstileRef.current?.reset();
    }, []);

    const resetQuoteVerificationSession = useCallback(() => {
        quoteVerificationSessionValueRef.current = null;
        quoteVerificationExpiresAtRef.current = null;
        setQuoteVerificationSession(null);
        setQuoteVerificationExpiresAt(null);
        quoteVerificationSessionPromiseRef.current = null;
    }, []);

    const settle = useCallback((value: string | null) => {
        if (resolveRef.current) {
            resolveRef.current(value);
            resolveRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!turnstileToken) return;
        setVerificationSuccess(true);
        const timer = window.setTimeout(() => {
            setIsVerifying(false);
            setIsVisible(false);
            setVerificationFailed(false);
            setVerificationSuccess(false);
            setFailCount(0);
            settle(turnstileToken);
        }, 250);
        return () => window.clearTimeout(timer);
    }, [settle, turnstileToken]);

    useEffect(() => {
        if (!quoteVerificationExpiresAt) return;
        const remainingMs = quoteVerificationExpiresAt - Date.now();
        if (remainingMs <= 0) {
            resetQuoteVerificationSession();
            return;
        }

        const timer = window.setTimeout(() => {
            resetQuoteVerificationSession();
        }, remainingMs);

        return () => window.clearTimeout(timer);
    }, [quoteVerificationExpiresAt, resetQuoteVerificationSession]);

    const getCurrentQuoteVerificationSession = useCallback(() => {
        const sessionToken = quoteVerificationSessionValueRef.current;
        const expiresAt = quoteVerificationExpiresAtRef.current;
        if (!sessionToken || !expiresAt || expiresAt <= Date.now()) {
            return null;
        }
        return sessionToken;
    }, []);

    const hasValidQuoteSession = Boolean(getCurrentQuoteVerificationSession());

    const verifyToken = useCallback((options?: VerificationOptions) => {
        const nextIntent = options?.intent || 'quote';
        const forceFresh = Boolean(options?.forceFresh);

        if (turnstileToken && !forceFresh) {
            return Promise.resolve(turnstileToken);
        }

        setIntent(nextIntent);
        setIsVisible(true);
        setIsVerifying(true);
        setVerificationFailed(false);
        setVerificationSuccess(false);
        if (forceFresh) {
            setTurnstileToken(null);
            setResetKey((prev) => prev + 1);
        }

        return new Promise<string | null>((resolve) => {
            resolveRef.current = resolve;
            window.setTimeout(() => {
                turnstileRef.current?.execute();
            }, 0);
        });
    }, [turnstileToken]);

    const verify = useCallback(async (options?: VerificationOptions) => {
        const token = await verifyToken(options);
        return Boolean(token);
    }, [verifyToken]);

    const createQuoteVerificationSession = useCallback(async (
        turnstileSessionToken: string,
        source: 'background' | 'interactive'
    ) => {
        const visitorId = getOrCreateVisitorId();
        const { data, error } = await supabase.functions.invoke('create-quote-verification-session', {
            body: {
                visitorId,
                turnstileToken: turnstileSessionToken,
            },
        });

        if (error) {
            throw error;
        }

        const sessionToken = typeof data?.verificationSession === 'string' ? data.verificationSession : null;
        const expiresAtValue = typeof data?.expiresAt === 'string' || typeof data?.expiresAt === 'number'
            ? new Date(data.expiresAt).getTime()
            : (Date.now() + QUOTE_VERIFICATION_SESSION_TTL_MS);

        if (!sessionToken || !Number.isFinite(expiresAtValue)) {
            throw new Error('invalid_quote_verification_session_response');
        }

        quoteVerificationSessionValueRef.current = sessionToken;
        quoteVerificationExpiresAtRef.current = expiresAtValue;
        setQuoteVerificationSession(sessionToken);
        setQuoteVerificationExpiresAt(expiresAtValue);
        void loadAnalytics().then(({ trackCanonicalFunnelStage }) => {
            trackCanonicalFunnelStage({
                stage: 'quote_security_verified',
                eventName: 'quote_security_verified',
                eventData: {
                    verification_source: source,
                },
            });
        });

        return sessionToken;
    }, [loadAnalytics]);

    const handleBackgroundTurnstileError = useCallback(() => {
        backgroundExecuteInFlightRef.current = false;
        backgroundWarmupBlockedUntilRef.current = Date.now() + 30_000;
        setShouldWarmQuoteVerification(false);
        setBackgroundResetKey((prev) => prev + 1);
    }, []);

    const handleQuoteVerificationSessionFailure = useCallback(() => {
        backgroundExecuteInFlightRef.current = false;
        backgroundWarmupBlockedUntilRef.current = Date.now() + 30_000;
        setShouldWarmQuoteVerification(false);
        setTurnstileToken(null);
        setVerificationSuccess(false);
        setVerificationFailed(true);
        setIsVerifying(false);
        setIsVisible(true);
    }, []);

    const beginQuoteVerificationSession = useCallback((
        turnstileSessionToken: string,
        source: 'background' | 'interactive'
    ) => {
        const sessionPromise = (async () => {
            try {
                return await createQuoteVerificationSession(turnstileSessionToken, source);
            } catch (error) {
                console.error('Failed to create quote verification session:', error);
                resetQuoteVerificationSession();
                if (source === 'interactive') {
                    handleQuoteVerificationSessionFailure();
                } else {
                    handleBackgroundTurnstileError();
                }
                return null;
            } finally {
                if (source === 'background') {
                    backgroundExecuteInFlightRef.current = false;
                }
                quoteVerificationSessionPromiseRef.current = null;
                setShouldWarmQuoteVerification(false);
            }
        })();

        quoteVerificationSessionPromiseRef.current = sessionPromise;
        return sessionPromise;
    }, [
        createQuoteVerificationSession,
        handleBackgroundTurnstileError,
        handleQuoteVerificationSessionFailure,
        resetQuoteVerificationSession,
    ]);

    const getQuoteVerificationSession = useCallback(async (options?: { forceFresh?: boolean }) => {
        const forceFresh = Boolean(options?.forceFresh);
        const currentSession = getCurrentQuoteVerificationSession();

        if (currentSession && !forceFresh) {
            return currentSession;
        }

        if (quoteVerificationSessionPromiseRef.current && !forceFresh) {
            return quoteVerificationSessionPromiseRef.current;
        }

        resetQuoteVerificationSession();
        const sessionPromise = (async () => {
            const token = await verifyToken({ intent: 'quote', forceFresh: true });
            if (!token) return null;
            return beginQuoteVerificationSession(token, 'interactive');
        })();

        quoteVerificationSessionPromiseRef.current = sessionPromise;
        return sessionPromise;
    }, [
        beginQuoteVerificationSession,
        getCurrentQuoteVerificationSession,
        resetQuoteVerificationSession,
        verifyToken,
    ]);

    const ensureQuoteVerificationSession = useCallback(async (options?: { forceFresh?: boolean }) => {
        const session = await getQuoteVerificationSession(options);
        return Boolean(session);
    }, [getQuoteVerificationSession]);

    const handleBackgroundTurnstileVerify = useCallback((token: string) => {
        backgroundExecuteInFlightRef.current = false;
        void beginQuoteVerificationSession(token, 'background');
    }, [beginQuoteVerificationSession]);

    useEffect(() => {
        if (
            !shouldWarmQuoteVerification ||
            shouldRenderTurnstile ||
            !TurnstileComponent ||
            hasValidQuoteSession ||
            quoteVerificationSessionPromiseRef.current ||
            backgroundExecuteInFlightRef.current
        ) {
            return;
        }

        const timer = window.setTimeout(() => {
            backgroundExecuteInFlightRef.current = true;
            backgroundTurnstileRef.current?.execute();
        }, 0);

        return () => window.clearTimeout(timer);
    }, [TurnstileComponent, hasValidQuoteSession, shouldRenderTurnstile, shouldWarmQuoteVerification]);

    const handleTurnstileError = useCallback(() => {
        setVerificationFailed(true);
        setVerificationSuccess(false);
        setIsVerifying(false);
        setFailCount((prev) => {
            const next = prev + 1;
            if (intent === 'quote') {
                const now = Date.now();
                const lastEvent = lastTurnstileErrorEventRef.current;
                const isDuplicateError =
                    lastEvent &&
                    lastEvent.intent === intent &&
                    (now - lastEvent.at) < 2000;

                if (!isDuplicateError) {
                    lastTurnstileErrorEventRef.current = { intent, at: now };
                    void loadAnalytics().then(({ trackEvent }) => trackEvent('quote_turnstile_error', { attempt: next }));
                }
            }
            return next;
        });
        setTurnstileToken(null);
    }, [intent, loadAnalytics]);

    const handleRetry = useCallback(() => {
        setVerificationFailed(false);
        setVerificationSuccess(false);
        setIsVerifying(true);
        setTurnstileToken(null);
        lastTurnstileErrorEventRef.current = null;
        setResetKey((prev) => prev + 1);
        window.setTimeout(() => {
            turnstileRef.current?.execute();
        }, 0);
    }, []);

    const fallbackHref = useMemo(() => {
        const isLT = window.location.pathname.startsWith('/lt');
        if (intent === 'careers') return isLT ? '/lt/karjera' : '/en/careers';
        return isLT ? '/lt/kontaktai' : '/en/contact';
    }, [intent]);

    const fallbackButtonLabel = useMemo(() => {
        if (intent === 'careers') return 'Open careers support';
        return verificationText?.contactPage || 'Open contact page';
    }, [intent, verificationText?.contactPage]);

    const closeToFallback = useCallback(() => {
        setIsVisible(false);
        setIsVerifying(false);
        settle(null);
        window.location.href = fallbackHref;
    }, [fallbackHref, settle]);

    const showFallback = failCount >= MAX_FAIL_ATTEMPTS;

    const prepareQuoteVerification = useCallback(() => {
        void import('../components/ui/Turnstile').then((module) => {
            setTurnstileComponent(() => module.Turnstile);
            void prefetchTurnstileScript().catch((error) => {
                console.warn('Failed to prefetch Turnstile script', error);
            });

            const isBlocked = backgroundWarmupBlockedUntilRef.current > Date.now();
            if (!hasValidQuoteSession && !quoteVerificationSessionPromiseRef.current && !isBlocked) {
                setShouldWarmQuoteVerification(true);
            }
        });
    }, [hasValidQuoteSession]);

    const verificationOverlay = shouldRenderTurnstile ? (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-stone-900/40 backdrop-blur-sm">
            <div className="relative w-full max-w-sm rounded-xl border border-stone-100 bg-white p-8 text-center shadow-2xl">
                {showFallback ? (
                    <>
                        <div className="mb-5 flex justify-center">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
                                <AlertTriangle size={32} className="text-amber-500" />
                            </div>
                        </div>
                        <h2 className="mb-2 text-xl font-bold text-stone-800">{verificationText?.unableTitle || 'Unable to verify'}</h2>
                        <p className="mb-6 text-sm text-stone-500">{verificationText?.unableMessage || 'We could not complete the security check.'}</p>
                        {intent !== 'careers' ? (
                            <div className="mb-6 space-y-3 text-left">
                                <div className="flex items-center gap-3 rounded-lg bg-stone-50 p-3">
                                    <Mail size={18} className="text-green-700" />
                                    <a href={`mailto:${CONTACT_INFO.email}`} className="text-sm font-medium text-stone-800">{CONTACT_INFO.email}</a>
                                </div>
                                <div className="flex items-center gap-3 rounded-lg bg-stone-50 p-3">
                                    <Phone size={18} className="text-green-700" />
                                    <a href={`tel:${CONTACT_INFO.phone.replace(/\s+/g, '')}`} className="text-sm font-medium text-stone-800">{CONTACT_INFO.phone}</a>
                                </div>
                            </div>
                        ) : null}
                        <button
                            onClick={closeToFallback}
                            className="w-full rounded-lg bg-green-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-green-800"
                        >
                            {fallbackButtonLabel}
                        </button>
                    </>
                ) : verificationSuccess ? (
                    <>
                        <div className="mb-6 flex justify-center">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
                                <Check size={32} className="text-green-500" />
                            </div>
                        </div>
                        <h2 className="mb-2 text-xl font-bold text-stone-800">{verificationText?.successTitle || 'Verified'}</h2>
                        <p className="text-sm text-stone-500">{verificationText?.successMessage || 'Security check completed.'}</p>
                    </>
                ) : verificationFailed ? (
                    <>
                        <div className="mb-5 flex justify-center">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                                <AlertTriangle size={32} className="text-red-400" />
                            </div>
                        </div>
                        <h2 className="mb-2 text-xl font-bold text-stone-800">{verificationText?.failedTitle || 'Verification failed'}</h2>
                        <p className="mb-6 text-sm text-stone-500">{verificationText?.failedMessage || 'Please try again.'}</p>
                        <button
                            onClick={handleRetry}
                            className="inline-flex items-center gap-2 rounded-lg bg-stone-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-stone-900"
                        >
                            <RefreshCw size={16} />
                            {verificationText?.retry || 'Try again'}
                        </button>
                    </>
                ) : (
                    <>
                        <div className="mb-6 flex justify-center">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-stone-50">
                                <ShieldCheck size={32} className="text-stone-600" />
                            </div>
                        </div>
                        <h2 className="mb-2 text-xl font-bold text-stone-800">{verificationText?.title || 'Security check'}</h2>
                        <p className="text-sm text-stone-500">{verificationText?.verifying || 'Verifying your submission...'}</p>
                    </>
                )}
                <div className="mt-4 flex justify-center">
                    {TurnstileComponent ? (
                        <TurnstileComponent
                            ref={turnstileRef}
                            key={resetKey}
                            siteKey={TURNSTILE_SITE_KEY}
                            onVerify={setTurnstileToken}
                            onExpire={() => setTurnstileToken(null)}
                            onError={handleTurnstileError}
                            appearance="execute"
                            action={`${intent}-submit`}
                            size="normal"
                        />
                    ) : (
                        <div className="h-[65px] w-[300px] animate-pulse rounded-lg bg-stone-100" />
                    )}
                </div>
            </div>
        </div>
    ) : null;

    return (
        <VerificationContext.Provider value={{
            turnstileToken,
            quoteVerificationSession,
            quoteVerificationExpiresAt,
            resetToken,
            resetQuoteVerificationSession,
            prepareQuoteVerification,
            verify,
            verifyToken,
            ensureQuoteVerificationSession,
            getQuoteVerificationSession,
            isVerifying,
        }}>
            {children}
            {shouldWarmQuoteVerification && TurnstileComponent && !shouldRenderTurnstile ? (
                <div className="pointer-events-none fixed -left-[9999px] top-0 h-px w-px overflow-hidden opacity-0">
                    <TurnstileComponent
                        ref={backgroundTurnstileRef}
                        key={`background-${backgroundResetKey}`}
                        siteKey={TURNSTILE_SITE_KEY}
                        onVerify={handleBackgroundTurnstileVerify}
                        onExpire={() => {
                            backgroundExecuteInFlightRef.current = false;
                            setShouldWarmQuoteVerification(false);
                            setBackgroundResetKey((prev) => prev + 1);
                        }}
                        onError={handleBackgroundTurnstileError}
                        appearance="execute"
                        action="quote-submit"
                        size="normal"
                    />
                </div>
            ) : null}
            {verificationOverlay && typeof document !== 'undefined'
                ? createPortal(verificationOverlay, document.body)
                : verificationOverlay}
        </VerificationContext.Provider>
    );
};
